import type { RouteLocationNormalized, RouteLocationResolved } from 'vue-router';
import { WorkspaceSwitchSupersededError } from '#layers/runtime/public';
import { isWorkspaceRoute } from '~/utils/routes';

interface WorkspaceSwitchDependencies {
	/** Resolves `'kept'` when the caller handled a failure and the active workspace stays published. */
	switchTo: (workspaceSlug: string) => Promise<'switched' | 'kept'>;
	resolve: (fullPath: string) => Pick<RouteLocationResolved, 'matched'>;
}

interface CreateWorkspaceSwitchMiddlewareOptions<TAbort> {
	abortNavigation: () => TAbort;
	resolveDependencies: () => WorkspaceSwitchDependencies;
}

/** Workspace route guard; dependencies resolve synchronously so injection-backed composables never run after the first await. */
export function createWorkspaceSwitchMiddleware<TAbort>(
	{ abortNavigation, resolveDependencies }: CreateWorkspaceSwitchMiddlewareOptions<TAbort>,
) {
	return async (to: RouteLocationNormalized) => {
		if (!isWorkspaceRoute(to)) return;

		const workspaceSlug = to.params.workspace;
		if (typeof workspaceSlug !== 'string') return abortNavigation();
		const dependencies = resolveDependencies();

		let outcome: 'switched' | 'kept';

		try {
			outcome = await dependencies.switchTo(workspaceSlug);
		}
		catch (error) {
			if (error instanceof WorkspaceSwitchSupersededError) return abortNavigation();
			throw error;
		}

		if (outcome === 'kept') return abortNavigation();

		const rematched = dependencies.resolve(to.fullPath).matched;
		const routeTableChanged = rematched.length !== to.matched.length
			|| rematched.some((record, index) => record !== to.matched[index]);

		if (routeTableChanged) {
			return {
				path: to.path,
				query: to.query,
				hash: to.hash,
				replace: true,
			};
		}
	};
}
